import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { LoginPage } from './components/Auth/LoginPage';
import { SignupPage } from './components/Auth/SignupPage';
import { OAuthCallback } from './components/Auth/OAuthCallback';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { LandingPageEnhanced } from './components/Landing/LandingPageEnhanced';
import { MainApp } from './components/MainApp';
import { AcceptInvitationPage } from './components/Organization/AcceptInvitationPage';
import { NotFound } from './components/Common/NotFound';
import { ViewSkeleton } from './components/Common/SuspenseFallback';
import { LoadingState } from './components/Common/StateDisplay';
import { WaitlistModal } from './components/Landing';
import { AccessibilityProvider } from './components/Accessibility/AccessibilityProvider';
import { logger } from './services/loggingService';
import { serviceInitializer } from './services/serviceInitializer';
import { sessionManager } from './services/sessionManager';
import { runStartupValidation } from './utils/schemaValidator';
import { supabase } from './lib/supabase';

// Initialize APM in production
if (import.meta.env.PROD) {
  import('./services/monitoring/apmService').then(({ APMService }) => {
    APMService.initialize();
    logger.info('APM Service initialized');
  }).catch(error => {
    logger.warn('Failed to initialize APM', { error });
  });
}

// Theme context for light/dark mode
export const ThemeContext = React.createContext({
  theme: 'light' as 'light' | 'dark',
  setTheme: (theme: 'light' | 'dark') => {},
});

function AppContent() {
  const { user, loading: authLoading, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [forceShowUI, setForceShowUI] = useState(false);
  const [servicesReady, setServicesReady] = useState(false);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  // Safety timeout: force show UI after 8s if still loading (prevent infinite hang)
  useEffect(() => {
    if (authLoading) {
      const timeout = setTimeout(() => {
        logger.warn(
          '[App] Force showing UI after 8s timeout - auth may still be loading'
        );
        setForceShowUI(true);
      }, 8000);
      return () => clearTimeout(timeout);
    } else {
      setForceShowUI(false); // Reset when auth finishes
    }
  }, [authLoading]);

  // All hooks must come before any conditional returns
  const [showWaitlist, setShowWaitlist] = useState(false);

  // Initialize services when user is authenticated
  useEffect(() => {
    let cleanup = false;
    
    const initializeServices = async () => {
      if (user && !servicesReady && !cleanup) {
        try {
          logger.info('[App] Initializing services for authenticated user');
          const success = await serviceInitializer.initialize();
          
          if (!cleanup) {
            if (success) {
              setServicesReady(true);
              logger.info('[App] Services initialized successfully');
              
              // Run startup validation in development mode
              if (import.meta.env.DEV) {
                runStartupValidation().catch(validationError => {
                  logger.warn('[App] Startup validation failed', { error: validationError });
                });
              }
            } else {
              // Check if failure is due to missing session
              const { data: { session }, error: sessionError } = await supabase.auth.getSession();
              if (!session || sessionError) {
                // No session or session error - sign out to clear user state and redirect to login
                logger.warn('[App] No session found after initialization failure - signing out user', { 
                  hasSession: !!session,
                  sessionError: sessionError?.message 
                });
                await signOut();
                return;
              }
              
              // Get more details about what failed
              const failedServices = serviceInitializer.getFailedServices();
              const allStatuses = serviceInitializer.getStatus() as any[];
              const failedDetails = allStatuses
                ?.filter((s: any) => failedServices.includes(s.name))
                .map((s: any) => `${s.name}${s.error ? `: ${s.error.message || s.error}` : ''}`)
                .join(', ') || 'unknown services';
              
              const errorMessage = failedServices.length > 0 
                ? `Failed to initialize: ${failedDetails}`
                : 'Failed to initialize application services';
              
              setServicesError(errorMessage);
              logger.error('[App] Service initialization failed', { 
                failedServices,
                allStatuses: allStatuses?.map((s: any) => ({
                  name: s.name,
                  initialized: s.initialized,
                  healthy: s.healthy,
                  error: s.error?.message
                }))
              });
            }
          }
        } catch (error) {
          if (!cleanup) {
            // Check if error is due to missing session
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (errorMessage.includes('No active session') || errorMessage.includes('session')) {
              logger.warn('[App] Initialization failed due to missing session - signing out user');
              await signOut();
              return;
            }
            
            setServicesError('An error occurred during initialization');
            logger.error('[App] Service initialization error', { error });
          }
        }
      }
    };
    
    initializeServices();
    
    return () => {
      cleanup = true;
      if (user) {
        // Cleanup services on unmount or user change
        serviceInitializer.cleanup(true);
      }
    };
  }, [user, servicesReady]);

  // Load theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (prefersDark) {
      setTheme('dark');
    }
  }, []);

  // Update theme in document and localStorage
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Show loading state while auth is loading
  if (authLoading && !forceShowUI) {
    return <LoadingState message="Authenticating..." size="large" fullScreen />;
  }

  // Show loading state while services are initializing
  if (user && !servicesReady && !servicesError) {
    return <LoadingState message="Initializing application..." size="large" fullScreen />;
  }

  // Show error if services failed to initialize
  if (user && servicesError) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Initialization Failed</h2>
          <p className="text-muted-foreground mb-4">{servicesError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <ProjectProvider>
        <div className="min-h-screen bg-background text-text">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={
              !user ? (
                <Suspense fallback={<ViewSkeleton />}>
                  <LandingPageEnhanced
                    onGetStarted={() => navigate('/login')}
                    onJoinWaitlist={() => setShowWaitlist(true)}
                  />
                  {showWaitlist && (
                    <WaitlistModal onClose={() => setShowWaitlist(false)} />
                  )}
                </Suspense>
              ) : (
                <Navigate to="/dashboard" replace />
              )
            } />
            
            <Route path="/login" element={
              <ProtectedRoute requireAuth={false}>
                <LoginPage onSwitchToSignup={() => navigate('/signup')} />
              </ProtectedRoute>
            } />
            
            <Route path="/signup" element={
              <ProtectedRoute requireAuth={false}>
                <SignupPage onSwitchToLogin={() => navigate('/login')} />
              </ProtectedRoute>
            } />

            <Route path="/cy-admin" element={
              <ProtectedRoute requireAuth={false}>
                <LoginPage onSwitchToSignup={() => navigate('/signup')} />
              </ProtectedRoute>
            } />

            {/* OAuth callbacks - no auth required */}
            <Route path="/auth/github/callback" element={
              <OAuthCallback provider='github' />
            } />
            <Route path="/auth/jira/callback" element={
              <OAuthCallback provider='jira' />
            } />

            {/* Organization invitation - no auth required */}
            <Route path="/organization/accept-invitation" element={
              <Suspense fallback={<ViewSkeleton />}>
                <AcceptInvitationPage />
              </Suspense>
            } />

            {/* Protected routes - require authentication */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />

            {/* All dashboard section routes */}
            <Route path="/agents" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/blueprint" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/build" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/code-review" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/review" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/execution" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/jira" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/tickets" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/usage" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/integrations" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/organization" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />

            {/* IDE route - can work with or without projectId */}
            <Route path="/ide/:projectId" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />
            <Route path="/ide" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />
            
            {/* Plan route - can work with or without projectId */}
            <Route path="/plan/:projectId" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />
            <Route path="/plan" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />

            {/* 404 - Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </ProjectProvider>
    </ThemeContext.Provider>
  );
}

export default function App() {
  return (
    <React.StrictMode>
      <AccessibilityProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </AccessibilityProvider>
    </React.StrictMode>
  );
}
